import BigNumber from 'bignumber.js';
import { type CurrencyMenuData, useStoreCurrencyMenu } from '~/stores/strapi/currencyMenu';

/**
 * Method for convert currency to standart, e. g.:
 * win:  1234, curenncy: USD => $12.34
 * win: 800, curenncy: BTC => 0.008 mBTC
 **/
const convertToShort = (
  value: number,
  currency: string,
  allCurrencies: CurrencyMenuData[]
): string => {
  const currencyData: CurrencyMenuData | undefined = Object.values(allCurrencies).find((item) => {
    return item.currencyCode === currency;
  });
  let precision = currencyData?.precision !== undefined ? currencyData.precision : 2;

  const mili = precision > 3;

  const amount = new BigNumber(value);

  const result = {
    amount: amount.shiftedBy(-precision).toString(),
    currency: currencyData?.currencyCodeShort || currencyData?.currencySymbol,
  };

  if (mili && amount.isGreaterThan(0)) {
    precision -= 3;
    result.amount = amount.shiftedBy(-precision).toString();
  }

  if (currencyData?.crypto) {
    return result.amount + ' ' + result.currency;
  } else {
    return result.currency + '' + result.amount;
  }
};

/**
 * Method for convert currency amount, e. g.:
 * win:  1230, curenncy: USD => 12.3
 * win: 875200, curenncy: BTC => 8.752
 **/
const convertToFormattedAmount = (
  value: number,
  currency: string,
  skipPrecision?: boolean,
  doNotShorten = false
) => {
  const storeCurrencyMenu = useStoreCurrencyMenu();

  let prefix = '';

  const currencyData = Object.values(storeCurrencyMenu.currencies).find((item) => {
    return item.currencyCode === currency;
  });

  const precision = currencyData?.precision !== undefined ? currencyData.precision : 2;
  const amount = new BigNumber(value);
  let result = skipPrecision ? amount : amount.shiftedBy(-precision);

  const shouldShorten =
    currencyData?.crypto && !['USDT'].includes(currencyData?.currencyCode || '') && !doNotShorten;

  if (shouldShorten) {
    if (result.toNumber() < 0.1 && result.toNumber() >= 0.0001) {
      result = result.multipliedBy(new BigNumber(10).pow(3));
      prefix = 'm';
    }

    if (result.toNumber() < 0.0001) {
      result = result.multipliedBy(new BigNumber(10).pow(6));
      prefix = 'μ';
    }
  }

  return {
    formattedAmount: String(
      parseFloat(Number(result.toString(10)).toFixed(currencyData?.precision || 2))
    ),
    formattedCurrency: `${prefix}${currency}`,
  };
};

export const useCurrencyConverter = () => {
  return {
    convertToShort,
    convertToFormattedAmount,
  };
};
